import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { collection, getDocs, doc, getDoc, setDoc, deleteDoc, query, limit } from 'firebase/firestore';
import { db } from '../firebase.js';
import useFirebaseCache from '../hooks/useFirebaseCache.js';

// Create the context
const InventoryContext = createContext(null);

// Custom hook to use the inventory context
export const useInventory = () => {
  const context = useContext(InventoryContext);
  if (!context) {
    throw new Error('useInventory must be used within an InventoryProvider');
  }
  return context;
};

// Provider component
export const InventoryProvider = ({ children, user }) => {
  const [ingredients, setIngredients] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const { 
    shouldRefreshCache, 
    hasCollectionChanged,
    getCachedData,
    setCachedData
  } = useFirebaseCache();

  // Load ingredients with caching
  const loadIngredients = useCallback(async () => {
    if (!user) {
      setIngredients([]);
      setInventory([]);
      setLoading(false);
      return [];
    }
    
    setLoading(true);
    const cachedIngredients = getCachedData('cachedIngredients', []);
    const cachedTimestamp = parseInt(localStorage.getItem('cachedIngredients_timestamp') || '0', 10);
    const needsRefresh = shouldRefreshCache('cachedIngredients', 120); // 2 hour cache
    
    try {
      const shouldFetch = !cachedIngredients.length || 
        (needsRefresh && await hasCollectionChanged('ingredients', cachedTimestamp));
        
      // Always load main ingredients
      let mainIngredients = [];
      if (!shouldFetch) {
        console.log('Using cached ingredients - unchanged since last fetch');
        mainIngredients = cachedIngredients;
      } else {
        console.log('Fetching fresh ingredients from Firestore');
        // Only select fields we actually need
        const q = query(
          collection(db, 'ingredients'), 
          limit(500)
        );
        
        const querySnapshot = await getDocs(q);
        mainIngredients = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setCachedData('cachedIngredients', mainIngredients);
      }

      // Always load user's custom ingredients if logged in - same optimization
      let userIngredients = [];
      if (user) {
        const cachedUserIngredients = getCachedData(`userIngredients_${user.uid}`, []);
        const userIngredientsTimestamp = parseInt(localStorage.getItem(`userIngredients_${user.uid}_timestamp`) || '0', 10);
        const needsUserRefresh = shouldRefreshCache(`userIngredients_${user.uid}`, 10); // 10 minute cache
        
        const userShouldFetch = !cachedUserIngredients.length || 
          (needsUserRefresh && await hasCollectionChanged(`users/${user.uid}/ingredients`, userIngredientsTimestamp));
        
        if (!userShouldFetch) {
          console.log('Using cached user ingredients - unchanged since last fetch');
          userIngredients = cachedUserIngredients;
        } else {
          console.log('Fetching fresh user ingredients from Firestore');
          // Only fetch fields we need
          const userIngredientsQuery = query(
            collection(db, `users/${user.uid}/ingredients`)
          );
          const userIngredientsSnapshot = await getDocs(userIngredientsQuery);
          userIngredients = userIngredientsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          
          setCachedData(`userIngredients_${user.uid}`, userIngredients);
        }
      }

      // Combine main and user ingredients
      const allIngredients = [...mainIngredients, ...userIngredients];
      setIngredients(allIngredients);
      setLoading(false);
      return allIngredients;
    } catch (error) {
      console.error('Error loading ingredients:', error);
      if (cachedIngredients.length) {
        setIngredients(cachedIngredients);
      }
      setLoading(false);
      return cachedIngredients;
    }
  }, [user, getCachedData, hasCollectionChanged, setCachedData, shouldRefreshCache]);

  // Load user inventory with caching
  const loadUserInventory = useCallback(async () => {
    if (!user) {
      setInventory([]);
      return;
    }
    
    const cachedInventory = getCachedData(`userInventory_${user.uid}`, []);
    const needsRefresh = shouldRefreshCache(`userInventory_${user.uid}`, 5); // 5 minute cache
    
    try {
      // Load user's inventory
      if (cachedInventory.length && !needsRefresh) {
        console.log('Using cached inventory');
        setInventory(cachedInventory);
        return;
      }
      
      console.log('Fetching fresh inventory from Firestore');
      // Load user's inventory
      const querySnapshot = await getDocs(collection(db, `users/${user.uid}/inventory`));
      const inventoryData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        possessed: true
      }));

      // Load all ingredients first
      const allIngredients = await loadIngredients();

      // Combine all ingredients with possession status and sort alphabetically
      const inventoryWithPossession = allIngredients
        .map(ingredient => ({
          ...ingredient,
          possessed: inventoryData.some(i => i.id === ingredient.id && i.possessed) || false
        }))
        .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));

      setInventory(inventoryWithPossession);
      setCachedData(`userInventory_${user.uid}`, inventoryWithPossession);
    } catch (error) {
      console.error('Error loading inventory:', error);
      if (cachedInventory.length) {
        setInventory(cachedInventory);
      } else {
        const allIngredients = await loadIngredients();
        setInventory(allIngredients.map(ingredient => ({ ...ingredient, possessed: false })));
      }
    }
  }, [user, getCachedData, loadIngredients, setCachedData, shouldRefreshCache]);

  // Toggle ingredient possession
  const togglePossession = useCallback(async (ingredientId) => {
    if (!user) {
      console.warn('User not logged in. Please log in to toggle possession.');
      return;
    }
    
    const newInventory = inventory.map(ing =>
      ing.id === ingredientId ? { ...ing, possessed: !ing.possessed } : ing
    );
    
    setInventory([...newInventory]);
    
    try {
      await setDoc(
        doc(db, `users/${user.uid}/inventory`, ingredientId),
        {
          ingredientId: ingredientId,
          possessed: newInventory.find(ing => ing.id === ingredientId)?.possessed
        },
        { merge: true }
      );
      
      // Update cache
      setCachedData(`userInventory_${user.uid}`, newInventory);
    } catch (error) {
      console.error('Error updating inventory in Firestore:', error);
      setInventory([...inventory]); // Revert to previous state on error
    }
  }, [inventory, setCachedData, user]);

  // Clean up unused ingredients
  const cleanupUnusedIngredients = useCallback(async (ingredientsToCheck) => {
    if (!user || !ingredientsToCheck || ingredientsToCheck.length === 0) return;
    
    console.log('Checking for unused ingredients:', ingredientsToCheck);
    
    try {
      // 1. Get all user recipes
      const recipesRef = collection(db, `users/${user.uid}/recipes`);
      const querySnapshot = await getDocs(recipesRef);
      const userRecipes = querySnapshot.docs.map(doc => doc.data());
      
      console.log('Found user recipes:', userRecipes.length);
      
      // 2. Create a set of all ingredients used in recipes
      const usedIngredients = new Set();
      userRecipes.forEach(recipe => {
        (recipe.ingredients || []).forEach(ing => {
          if (ing.ingredientId) {
            usedIngredients.add(ing.ingredientId);
          }
        });
      });
      
      console.log('Used ingredients:', Array.from(usedIngredients));
      
      // 3. Find ingredients that are no longer used
      const unusedIngredients = ingredientsToCheck.filter(id => !usedIngredients.has(id));
      
      console.log('Unused ingredients:', unusedIngredients);
      
      // 4. Delete unused ingredients (only if they're custom ingredients)
      for (const ingredientId of unusedIngredients) {
        // Check if this is a custom ingredient (not a standard one)
        // First, check if it exists in the user's ingredients collection
        const userIngredientRef = doc(db, `users/${user.uid}/ingredients`, ingredientId);
        const userIngredientDoc = await getDoc(userIngredientRef);
        
        if (userIngredientDoc.exists()) {
          console.log(`Found custom ingredient to delete: ${ingredientId}`);
          
          // Delete the ingredient
          await deleteDoc(userIngredientRef);
          
          // Also remove from inventory if it exists there
          const inventoryRef = doc(db, `users/${user.uid}/inventory`, ingredientId);
          const inventoryDoc = await getDoc(inventoryRef);
          
          if (inventoryDoc.exists()) {
            await deleteDoc(inventoryRef);
            console.log(`Removed ingredient from inventory: ${ingredientId}`);
          }
          
          console.log(`Deleted unused custom ingredient: ${ingredientId}`);
        } else {
          console.log(`Ingredient ${ingredientId} is not a custom ingredient or doesn't exist`);
        }
      }
      
      // 5. Update the inventory state to reflect the changes
      if (unusedIngredients.length > 0) {
        setInventory(prev => {
          const newInventory = prev.filter(ing => !unusedIngredients.includes(ing.id));
          console.log(`Updated inventory, removed ${prev.length - newInventory.length} items`);
          return newInventory;
        });
        
        // Update cache
        setCachedData(`userInventory_${user.uid}`, inventory.filter(ing => !unusedIngredients.includes(ing.id)));
      }
    } catch (error) {
      console.error('Error cleaning up unused ingredients:', error);
    }
  }, [inventory, setCachedData, user]);

  // Load data when user changes
  useEffect(() => {
    if (user) {
      loadIngredients().then(() => {
        loadUserInventory();
      });
    } else {
      setIngredients([]);
      setInventory([]);
      setLoading(false);
    }
  }, [user, loadIngredients, loadUserInventory]);

  // Create the context value object
  const inventoryContextValue = {
    ingredients,
    inventory,
    loading,
    togglePossession,
    cleanupUnusedIngredients,
    refreshInventory: loadUserInventory,
    refreshIngredients: loadIngredients
  };
  
  // Provide the context value to children
  return (
    <InventoryContext.Provider value={inventoryContextValue}>
      {children}
    </InventoryContext.Provider>
  );
}; 